import { useEffect, useMemo, useRef, useState } from 'react';
import { DS2 } from '../../../theme/designTokens';
import { useFriends } from '../../../context/FriendContext';
import { useChatDock } from '../../../context/ChatDockContext';
import { PlayerAvatar } from '../../ui/PlayerAvatar';
import { UnreadBadge } from '../../ui/UnreadBadge';
import { PresenceDot } from '../../social/PresenceDot';
import { relativeTimeShort } from '../../../utils/relativeTime';
import { useTranslation } from '../../../i18n/react';
import type { FriendListItem } from '../../../types/social';

/** The full sidebar is a fixed 190px column (see Sidebar.tsx) — the flyout
 *  opens just to its right, anchored to the bottom like chess.com's
 *  messages panel. */
const SIDEBAR_WIDTH_FULL = 190;
const PANEL_WIDTH = 300;

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width={13}
      height={13}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ transform: open ? 'none' : 'rotate(180deg)' }}
    >
      <path d="M6 9l6 6 6-6" stroke={DS2.color.gold} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PaperPlaneIcon() {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 3L10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5 21 3Z" stroke="#8A8A8A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const HEADER_TEXT_STYLE: React.CSSProperties = {
  fontFamily:    DS2.font.heading,
  fontSize:      11.5,
  fontWeight:    700,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  color:         DS2.color.gold,
};

/**
 * Desktop Chat entry (Docked Chat Redesign — chess.com flyout pattern).
 * The sidebar carries only a compact CHAT button with the total unread
 * badge; clicking it opens a floating messages panel anchored to the
 * bottom-left of the page (beside the sidebar) with the full conversation
 * list — avatar, presence, name, last-message preview, per-conversation
 * unread — plus a "+ New Chat" friend picker. Selecting a conversation
 * opens/focuses its docked window through ChatDockContext and closes the
 * panel; nothing here ever marks a conversation read.
 *
 * Hidden entirely on suppressed routes (game/spectate/tutorial) and below
 * the desktop dock breakpoint (tablet/mobile keep their existing Messages
 * navigation).
 */
export function ChatSidebarModule() {
  const { t } = useTranslation('messages');
  // Defensive defaults: several long-standing shell tests mock useFriends()
  // with a partial value; the real provider always supplies these.
  const {
    conversations: conversationsRaw,
    conversationsLoading,
    friends: friendsRaw,
    unreadMessageCount = 0,
    hideConversation,
  } = useFriends();
  const conversations = conversationsRaw ?? [];
  const friends = friendsRaw ?? [];
  const {
    isDesktopDock, suppressed, moduleExpanded, setModuleExpanded,
    openConversation, registerModuleHeader,
  } = useChatDock();

  // Panel-internal mode: conversation list ⇄ new-chat friend picker.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Close on outside click — the flyout floats over the page content.
  useEffect(() => {
    if (!moduleExpanded) return;
    function onOutside(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setModuleExpanded(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [moduleExpanded, setModuleExpanded]);

  // Reset the picker whenever the panel closes.
  useEffect(() => {
    if (!moduleExpanded) {
      setPickerOpen(false);
      setPickerQuery('');
    }
  }, [moduleExpanded]);

  const sorted = useMemo(
    () =>
      [...conversations].sort((a, b) => {
        const at = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const bt = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return bt - at;
      }),
    [conversations],
  );

  const pickerFriends = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    return friends.filter(f => !q || f.friend.display_name.toLowerCase().includes(q));
  }, [friends, pickerQuery]);

  function statusFor(friendUserId: number): FriendListItem['status'] | undefined {
    return friends.find(f => f.friend.id === friendUserId)?.status;
  }

  function handleOpen(friendId: number) {
    openConversation(friendId);
    setModuleExpanded(false);  // chess.com pattern: picking a chat closes the panel
  }

  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    setModuleExpanded(false);
    buttonRef.current?.focus();
  }

  if (!isDesktopDock || suppressed) return null;

  return (
    <>
      {/* Compact CHAT button — the only chrome the sidebar itself carries. */}
      <section aria-label={t('chat.title')} style={{ marginBottom: 14 }}>
        <button
          type="button"
          ref={el => { buttonRef.current = el; registerModuleHeader(el); }}
          aria-expanded={moduleExpanded}
          aria-haspopup="dialog"
          aria-label={moduleExpanded ? t('chat.collapse') : t('chat.expand')}
          onClick={() => setModuleExpanded(!moduleExpanded)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, width: '100%',
            padding: '9px 14px', background: '#101010',
            border: '1px solid rgba(201,168,76,0.45)', borderRadius: 12,
            cursor: 'pointer', boxSizing: 'border-box',
          }}
        >
          <span style={HEADER_TEXT_STYLE}>{t('chat.title')}</span>
          {unreadMessageCount > 0 && (
            <span aria-label={t('chat.unreadAria', { count: unreadMessageCount })}>
              <UnreadBadge count={unreadMessageCount} />
            </span>
          )}
          <span style={{ flex: 1 }} />
          <ChevronIcon open={moduleExpanded} />
        </button>
      </section>

      {/* Floating messages panel (chess.com style) — fixed to the viewport
          beside the sidebar, so it grows over the page content instead of
          fighting the sidebar for vertical space. */}
      {moduleExpanded && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label={t('chat.title')}
          onKeyDown={handlePanelKeyDown}
          style={{
            position:      'fixed',
            left:          SIDEBAR_WIDTH_FULL + 12,
            bottom:        16,
            width:         PANEL_WIDTH,
            maxHeight:     'min(480px, calc(100dvh - 32px))',
            display:       'flex',
            flexDirection: 'column',
            background:    '#101010',
            border:        '1px solid rgba(201,168,76,0.45)',
            borderRadius:  12,
            boxShadow:     '0 12px 40px rgba(0,0,0,0.6)',
            zIndex:        46,
            overflow:      'hidden',
          }}
        >
          {/* Panel header */}
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 14px 9px', borderBottom: '1px solid rgba(201,168,76,0.25)',
              flexShrink: 0,
            }}
          >
            {pickerOpen && (
              <button
                type="button"
                aria-label={t('back')}
                onClick={() => { setPickerOpen(false); setPickerQuery(''); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: 6, border: 'none',
                  background: 'transparent', cursor: 'pointer', padding: 0,
                }}
              >
                <BackIcon />
              </button>
            )}
            <span style={HEADER_TEXT_STYLE}>{pickerOpen ? t('chat.newChat') : t('chat.title')}</span>
            {!pickerOpen && unreadMessageCount > 0 && (
              <span aria-label={t('chat.unreadAria', { count: unreadMessageCount })}>
                <UnreadBadge count={unreadMessageCount} />
              </span>
            )}
            <span style={{ flex: 1 }} />
            <button
              type="button"
              aria-label={t('chat.collapse')}
              onClick={() => { setModuleExpanded(false); buttonRef.current?.focus(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 22, height: 22, borderRadius: 6, border: 'none',
                background: 'transparent', cursor: 'pointer', padding: 0,
              }}
            >
              <ChevronIcon open={false} />
            </button>
          </div>

          {pickerOpen ? (
            <>
              {/* New chat — search across existing friends */}
              <div style={{ padding: 10, borderBottom: '1px solid rgba(245,245,245,0.08)', flexShrink: 0 }}>
                <input
                  type="text"
                  autoFocus
                  value={pickerQuery}
                  onChange={e => setPickerQuery(e.target.value)}
                  placeholder={t('findFriendPlaceholder')}
                  style={{
                    width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
                    background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 10px',
                    color: '#FFFFFF', fontFamily: DS2.font.body, fontSize: 13,
                  }}
                />
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {pickerFriends.length === 0 ? (
                  <div style={{ padding: '14px 16px', fontFamily: DS2.font.body, fontSize: 12.5, color: '#A89F86' }}>
                    {t('noFriendsFound')}
                  </div>
                ) : (
                  pickerFriends.map(f => (
                    <button
                      type="button"
                      key={f.friendship_id}
                      onClick={() => handleOpen(f.friend.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '9px 14px', background: 'transparent', border: 'none',
                        cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <span style={{ position: 'relative', flexShrink: 0 }}>
                        <PlayerAvatar avatarKey={f.friend.avatar_key} name={f.friend.display_name} size={26} />
                        <PresenceDot
                          status={f.status}
                          size={9}
                          border="2px solid #101010"
                          style={{ position: 'absolute', right: -1, bottom: -1 }}
                        />
                      </span>
                      <span style={{ fontFamily: DS2.font.body, fontSize: 13, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.friend.display_name}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              {/* Conversation list — loading / empty / rows */}
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {conversationsLoading && conversations.length === 0 ? (
                  <div style={{ padding: '14px 14px', fontFamily: DS2.font.body, fontSize: 12.5, color: '#A89F86' }}>
                    {t('chat.loading')}
                  </div>
                ) : sorted.length === 0 ? (
                  <div style={{ padding: '26px 16px 24px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                      <PaperPlaneIcon />
                    </div>
                    <p style={{ margin: 0, fontFamily: DS2.font.body, fontSize: 13, fontWeight: 600, color: '#FFFFFF' }}>
                      {t('chat.empty')}
                    </p>
                    <p style={{ margin: '4px 0 0', fontFamily: DS2.font.body, fontSize: 11.5, color: '#8A8A8A' }}>
                      {t('chat.emptyHint')}
                    </p>
                  </div>
                ) : (
                  sorted.map(c => {
                    const status = statusFor(c.friend.id);
                    const unread = c.unread_count > 0;
                    const previewBody = c.last_message || (c.last_message_has_image ? t('previewPhoto') : '');
                    const preview = previewBody
                      ? (c.last_from_me ? t('previewYou', { text: previewBody }) : previewBody)
                      : t('previewEmpty');
                    return (
                      <div
                        key={c.friend.id}
                        style={{
                          display: 'flex', alignItems: 'center',
                          borderBottom: '1px solid rgba(245,245,245,0.05)',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleOpen(c.friend.id)}
                          aria-label={`${t('chat.conversationAria', { name: c.friend.display_name })}${unread ? ` — ${t('chat.unreadAria', { count: c.unread_count })}` : ''}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0,
                            padding: '9px 6px 9px 14px', background: 'transparent', border: 'none',
                            cursor: 'pointer', textAlign: 'left',
                          }}
                        >
                          <span style={{ position: 'relative', flexShrink: 0 }}>
                            <PlayerAvatar avatarKey={c.friend.avatar_key} name={c.friend.display_name} size={34} />
                            {status && (
                              <PresenceDot
                                status={status}
                                size={10}
                                border="2px solid #101010"
                                style={{ position: 'absolute', right: -1, bottom: -1 }}
                              />
                            )}
                          </span>
                          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                              <span
                                style={{
                                  fontFamily: DS2.font.body, fontSize: 13, fontWeight: unread ? 700 : 500,
                                  color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}
                              >
                                {c.friend.display_name}
                              </span>
                              {c.last_message_at && (
                                <span style={{ flexShrink: 0, fontFamily: DS2.font.body, fontSize: 10.5, color: unread ? '#D4AF37' : '#777777' }}>
                                  {relativeTimeShort(c.last_message_at)}
                                </span>
                              )}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 1 }}>
                              <span
                                style={{
                                  fontFamily: DS2.font.body, fontSize: 11.5, fontWeight: unread ? 600 : 400,
                                  color: unread ? '#E8E2D0' : '#8A8A8A',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}
                              >
                                {preview}
                              </span>
                              {unread && <UnreadBadge count={c.unread_count} />}
                            </span>
                          </span>
                        </button>
                        {/* "Remove from my list" — hides ONLY this user's list
                            entry (history intact; a newer message brings the
                            conversation back). Panel stays open for tidying
                            several in a row. */}
                        <button
                          type="button"
                          aria-label={`${t('chat.removeFromList')} — ${c.friend.display_name}`}
                          title={t('chat.removeFromList')}
                          onClick={() => void hideConversation?.(c.friend.id)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 26, height: 26, marginRight: 6, borderRadius: 6, border: 'none',
                            background: 'transparent', cursor: 'pointer', flexShrink: 0,
                          }}
                        >
                          <svg width={11} height={11} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M5 5l14 14M19 5L5 19" stroke="#8A8A8A" strokeWidth="2.2" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* + New Chat — bottom action swaps the panel into the picker */}
              <button
                type="button"
                aria-label={t('chat.newChat')}
                onClick={() => setPickerOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  width: '100%', padding: '10px 14px', background: 'transparent',
                  border: 'none', borderTop: '1px solid rgba(201,168,76,0.25)',
                  cursor: 'pointer', fontFamily: DS2.font.body, fontSize: 12.5, fontWeight: 600,
                  color: '#FFFFFF', flexShrink: 0,
                }}
              >
                {`+ ${t('chat.newChat')}`}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
